import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { VOCAB_CARDS } from "../data/vocabData";

// ─── Image loader ──────────────────────────────────────────────────────────────
const VOCAB_IMG = {};
try {
    const ctx = require.context("../assets/images/vocab", false, /\.(png|jpe?g|PNG|JPG|JPEG)$/i);
    ctx.keys().forEach((k) => {
        const name = k.replace(/^\.\//, "").replace(/\.(png|jpe?g|PNG|JPG|JPEG)$/i, "").normalize("NFC");
        VOCAB_IMG[name] = ctx(k);
    });
} catch (_) {}

// ─── Card audio loader ─────────────────────────────────────────────────────────
// Stores both base ("ame") and indexed ("ame1", "ame2") keys so multi-meaning
// cards can each get their own audio.
const VOCAB_AUDIO = {};
try {
    const aCtx = require.context("../assets/audio/vocab", false, /\.(mp3|m4a|MP3|M4A)$/i);
    aCtx.keys().forEach((k) => {
        const raw  = k.replace(/^\.\//, "").replace(/\..+$/, "").toLowerCase();
        // Store under both NFC and NFD so macOS filesystem differences never block lookup
        const nfc  = raw.normalize("NFC");
        const nfd  = raw.normalize("NFD");
        const src  = aCtx(k);
        [nfc, nfd].forEach((name) => {
            const base = name.replace(/(_\d+|\d+)$/, "");
            if (!VOCAB_AUDIO[base]) VOCAB_AUDIO[base] = src;
            VOCAB_AUDIO[name] = src;
        });
    });
} catch (_) {}

// ─── Per-key card indices ──────────────────────────────────────────────────────
// Assign 1-based index to each card within its key group so duplicate-key
// cards (e.g. two "ame" cards for rain vs candy) get separate audio/images.
const _keyCount = {};
const VOCAB_CARDS_INDEXED = VOCAB_CARDS.map((card) => {
    _keyCount[card.key] = (_keyCount[card.key] || 0) + 1;
    return { ...card, _idx: _keyCount[card.key] };
});

// ─── App sounds + BGM loader ───────────────────────────────────────────────────
let TAP_SND = null, CORRECT_SND = null, WRONG_SND = null, COUNTDOWN_SND = null;
let VOCAB_BGM_SRC = null, RESULT_BGM_SRC = null;
const GOODJOB_AUDIO = { en: null, zh: null, jp: null };
try {
    const aCtx = require.context("../assets/audio", false, /\.(mp3|m4a|MP3|M4A)$/i);
    aCtx.keys().forEach((k) => {
        const name = k.replace(/^\.\//, "").replace(/\..+$/, "").toLowerCase();
        if (name === "tap")            TAP_SND        = aCtx(k);
        if (name === "correct")        CORRECT_SND    = aCtx(k);
        if (name === "wrong")          WRONG_SND      = aCtx(k);
        if (name === "countdown")      COUNTDOWN_SND  = aCtx(k);
        if (name === "vocabulary_bgm") VOCAB_BGM_SRC  = aCtx(k);
        if (name === "hiraganaquiz_bgm") RESULT_BGM_SRC = aCtx(k);
        const mGj = k.match(/[/\\]goodjob_(en|eng|zh|jp)\.\w+$/i);
        if (mGj) { const gl = mGj[1].toLowerCase(); GOODJOB_AUDIO[gl === "eng" ? "en" : gl] = aCtx(k); return; }
        if (/[/\\]よくできました\.\w+$/i.test(k)) { GOODJOB_AUDIO.jp = aCtx(k); return; }
        if (/[/\\]不错不错\.\w+$/i.test(k))       { GOODJOB_AUDIO.zh = aCtx(k); return; }
        const mGj2 = k.match(/[/\\]good[\s_]?job[^/\\]*\.\w+$/i);
        if (mGj2 && !GOODJOB_AUDIO.en) { GOODJOB_AUDIO.en = aCtx(k); }
    });
} catch (_) {}

function playSound(src, volume = 1) {
    if (!src) return null;
    try { const a = new Audio(src); a.volume = volume; a.play().catch(() => {}); return a; }
    catch (_) { return null; }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
// cardIdx: 1-based index within cards sharing the same key (default 1).
// For the 2nd "ame" card (candy), cardIdx=2 → primary image is ame_2.png.
function getImg(key, slot, cardIdx = 1) {
    const k = (key || "").normalize("NFC");
    // Fullwidth digit equivalents for Japanese-named files (e.g. ちいさい１.png)
    const FW = ['０','１','２','３','４','５','６','７','８','９'];
    const fwSlot = FW[slot] || slot;

    if (cardIdx > 1) {
        // For subsequent duplicates shift the image slot so card 2 uses ame_2.png etc.
        const shifted = (cardIdx - 1) + slot;
        const fwShifted = FW[shifted] || shifted;
        return VOCAB_IMG[`${k}_${shifted}`]
            || VOCAB_IMG[`${k}${shifted}`]
            || VOCAB_IMG[`${k}${fwShifted}`]
            || VOCAB_IMG[`${k}_${cardIdx}`]
            || VOCAB_IMG[`${k}${cardIdx}`]
            || null;
    }
    return VOCAB_IMG[`${k}_${slot}`]
        || VOCAB_IMG[`${k}${slot}`]
        || VOCAB_IMG[`${k}${fwSlot}`]
        || (slot === 1 ? VOCAB_IMG[k] : null)
        || null;
}

// card can be a card object (with _idx) or a plain key string (legacy).
function getCardImages(card) {
    const key = typeof card === "string" ? card : card.key;
    const idx = typeof card === "string" ? 1 : (card._idx || 1);
    const img1 = getImg(key, 1, idx);
    const img2 = getImg(key, 2, idx) || img1;
    return { img1, img2 };
}

// Get audio src for a card, preferring the indexed version (ame1 for idx=1, ame2 for idx=2).
function getAudioSrc(card) {
    const idx    = card._idx || 1;
    const key    = (card.key || "").toLowerCase().normalize("NFC");
    const romaji = (card.romaji || "").toLowerCase().normalize("NFC");
    return VOCAB_AUDIO[`${key}${idx}`]
        || VOCAB_AUDIO[`${key}_${idx}`]
        || VOCAB_AUDIO[key]
        || (romaji && (VOCAB_AUDIO[`${romaji}${idx}`] || VOCAB_AUDIO[romaji]))
        || null;
}

// ─── Hanamaru video ────────────────────────────────────────────────────────────
let HANAMARU_VIDEO = null;
try {
    const gifReq = require.context("../assets/gif", false, /\.(mp4|webm)$/i);
    gifReq.keys().forEach((k) => {
        if (/[/\\]hanamaru\.\w+$/i.test(k)) HANAMARU_VIDEO = gifReq(k);
    });
} catch (_) {}

function HanamaruCanvas({ videoSrc, play = true }) {
    const videoRef  = useRef(null);
    const canvasRef = useRef(null);
    const rafRef    = useRef(null);
    useEffect(() => {
        const video = videoRef.current, canvas = canvasRef.current;
        if (!video || !canvas) return;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        const processFrame = () => {
            if (video.readyState >= 2 && !video.paused && !video.ended) {
                const w = canvas.width, h = canvas.height;
                ctx.drawImage(video, 0, 0, w, h);
                const img = ctx.getImageData(0, 0, w, h);
                const d = img.data;
                for (let i = 0; i < d.length; i += 4) {
                    const r = d[i], g = d[i+1], b = d[i+2];
                    if (g > 80 && g > r * 1.15 && g > b * 1.1) d[i+3] = 0;
                }
                ctx.putImageData(img, 0, 0);
            }
            rafRef.current = requestAnimationFrame(processFrame);
        };
        const onMeta = () => { if (video.videoWidth) { canvas.width = video.videoWidth; canvas.height = video.videoHeight; } };
        const onPlay = () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); rafRef.current = requestAnimationFrame(processFrame); };
        const onEnd  = () => { if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; } };
        video.addEventListener("loadedmetadata", onMeta);
        video.addEventListener("play", onPlay);
        video.addEventListener("ended", onEnd);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            video.removeEventListener("loadedmetadata", onMeta);
            video.removeEventListener("play", onPlay);
            video.removeEventListener("ended", onEnd);
            video.pause();
        };
    }, [videoSrc]);
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        if (play) v.play().catch(() => {}); else v.pause();
    }, [play]);
    return (
        <div className="pointer-events-none flex items-center justify-center">
            <video ref={videoRef} src={videoSrc} playsInline muted
                style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }} />
            <canvas ref={canvasRef} width={480} height={480}
                style={{ maxWidth: "min(320px, 85vw)", height: "auto", display: "block" }} />
        </div>
    );
}

function shufflePick(arr, n) {
    const withImg = arr.filter((v) => getImg(v.key, 1, v._idx || 1));
    const copy = [...withImg];
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy.slice(0, Math.min(n, copy.length));
}

// ─── i18n ─────────────────────────────────────────────────────────────────────
const V_I18N = {
    JP: {
        title:           "これにほんごでなんていう？",
        titleJpFront:    "どういういみ？",
        howToPlay:       "にほんごでなんていうかわかったらカードをタップしてこたえをみてね",
        howToPlayJpFront:"いみがわかったらカードをタップしてこたえを見てね",
        howToPlay2:      "あってたらうえにスワイプ、まちがってたらしたにスワイプしてね",
        howToPlayPC:     "あってたら「あってた！」ボタン、まちがってたら「わからなかった…」ボタンをおしてね",
        start:           "スタート",
        hintUp:          "あってた！",
        hintDown:        "わからなかった…",
        btnKnow:         "わかった",
        btnNoIdea:       "わからなかった…",
        goodjob:         "よくできました！🎉",
        perfect:         "パーフェクト！",
        great:           "よくできた！",
        tryHard:         "がんばろう！",
        tryAgain:        "もういっかいやってみよう！",
        reviewLabel:     "もういっかいおぼえよう",
        playAgain:       "もういっかい",
        frontLabel:      "をおもてにする",
        jpFront:         "にほんご",
        enFront:         "えいご・ちゅうごくご",
        copyright:       "このサイトの画像・イラスト・学習コンテンツはすべて著作権により保護されており、書面による許可なく複製・転載・配布することを禁止します。",
        tapHint:         "わかったらタップしてね",
    },
    ZH: {
        title:           "用日语怎么说？",
        titleJpFront:    "这是什么意思？",
        howToPlay:       "知道日语怎么说就点击卡片查看答案！",
        howToPlayJpFront:"知道是什么意思就点击卡片查看答案！",
        howToPlay2:      "答对了向上划，答错了向下划",
        howToPlayPC:     "答对了点「答对了！」按钮，答错了点「答错了」按钮",
        start:           "开始",
        hintUp:          "答对了！",
        hintDown:        "答错了",
        btnKnow:         "答对了！",
        btnNoIdea:       "答错了",
        goodjob:         "太棒了！🎉",
        perfect:         "满分！",
        great:           "做得好！",
        tryHard:         "继续努力！",
        tryAgain:        "再试一次！",
        reviewLabel:     "再复习一遍",
        playAgain:       "再来一次",
        frontLabel:      "在前面显示",
        jpFront:         "日语",
        enFront:         "英语・中文",
        copyright:       "本网站的图片、插图及学习内容均受版权保护，未经书面许可，不得复制、转载或分发。",
        tapHint:         "知道答案就点一下！",
    },
    EN: {
        title:           "Can you say this in Japanese?",
        titleJpFront:    "What does this mean?",
        howToPlay:       "If you know the Japanese, tap the card to see answer!",
        howToPlayJpFront:"If you know what it means, tap the card to see answer!",
        howToPlay2:      "Swipe up if you got it right, down if you got it wrong",
        howToPlayPC:     "Click \"Got it right!\" if correct, \"Got it wrong\" if not",
        start:           "Start",
        hintUp:          "Got it right!",
        hintDown:        "Got it wrong",
        btnKnow:         "Got it right!",
        btnNoIdea:       "Got it wrong",
        goodjob:         "Well done! 🎉",
        perfect:         "Perfect!",
        great:           "Great job!",
        tryHard:         "Keep it up!",
        tryAgain:        "Try again!",
        reviewLabel:     "Review these",
        playAgain:       "Play again",
        frontLabel:      "on front",
        jpFront:         "Japanese",
        enFront:         "Eng & Chinese",
        copyright:       "All images, illustrations, and educational content on this site are protected by copyright and may not be copied, reproduced, or distributed without written permission.",
        tapHint:         "tap to reveal!",
    },
};

const PILE_ROTS = [-4, 2, -1.5, 3, -2.5, 0.5, -3, 1, -1, 2.5];
const PILE_OFFX = [-6, 4, -3, 7, -5, 2, -4, 5, -2, 3];
const TOTAL      = 10;
const TIMER_SECS = 10;

// ─── Component ────────────────────────────────────────────────────────────────
const VOCAB_PAGE_META = {
    EN: {
        title: "Swipe & Learn Words! | Hanapin Japanese Vocabulary Flashcards",
        desc:  "Learn Japanese vocabulary with interactive swipe flashcards. Practice hiragana words with images and audio!",
    },
    JP: {
        title: "これ、いえる？単語スワイプ | はなぴん",
        desc:  "単語カードをスワイプして、日本語の語彙をどんどん増やそう！",
    },
    ZH: {
        title: "刷卡学日语 | 樱花老师 日语单词卡",
        desc:  "滑动单词卡，快速积累日语词汇！",
    },
};

export default function Vocabulary({ language = "EN" }) {
    const t         = V_I18N[language] || V_I18N.EN;
    const fontClass = language === "ZH" ? "font-zh" : language === "JP" ? "font-jp" : "font-en";

    const [phase,      setPhase]      = useState("pile");
    const [frontMode,  setFrontMode]  = useState("en");  // "en" = Eng&Chinese front | "jp" = Japanese front
    const [cards,      setCards]      = useState([]);
    const [cardIdx,    setCardIdx]    = useState(0);
    const [revealed,   setRevealed]   = useState(false);
    const [score,      setScore]      = useState(0);
    const [timeLeft,   setTimeLeft]   = useState(TIMER_SECS);
    const [swipeDir,   setSwipeDir]   = useState(null);
    const [wrongCards, setWrongCards] = useState([]);
    const [pileCards,  setPileCards]  = useState(() => shufflePick(VOCAB_CARDS_INDEXED, TOTAL));
    const [animFrame,  setAnimFrame]  = useState(0);
    const [pileAnim,   setPileAnim]   = useState(0);
    const [isShuffling, setIsShuffling] = useState(false);
    const [showCopyToast, setShowCopyToast] = useState(false);
    const copyToastRef = useRef(null);

    // ── Image right-click protection ──────────────────────────────────────────
    const blockContextMenu = (e) => {
        e.preventDefault();
        if (copyToastRef.current) clearTimeout(copyToastRef.current);
        setShowCopyToast(true);
        copyToastRef.current = setTimeout(() => setShowCopyToast(false), 3000);
    };

    // ── Per-page SEO ──────────────────────────────────────────────────────────
    useEffect(() => {
        const meta = VOCAB_PAGE_META[language] || VOCAB_PAGE_META.EN;
        document.title = meta.title;
        const el = document.querySelector('meta[name="description"]');
        if (el) el.setAttribute("content", meta.desc);
    }, [language]);

    const timerRef           = useRef(null);
    const swipingRef         = useRef(false);
    const audioPlayingRef    = useRef(false);
    const countdownPlayedRef = useRef(false);
    const pointerStart       = useRef(null);
    const handleSwipeRef     = useRef(null);
    const handleTimeoutRef   = useRef(null);
    const cardAudioRef       = useRef(null);
    const bgmRef             = useRef(null);
    const resultAudioRef     = useRef(null);

    // ── BGM helpers ───────────────────────────────────────────────────────────
    const stopBGM = useCallback(() => {
        if (bgmRef.current) {
            bgmRef.current.pause();
            bgmRef.current.currentTime = 0;
            bgmRef.current = null;
        }
    }, []);

    const startBGM = useCallback((src, volume = 0.45) => {
        stopBGM();
        if (!src) return;
        try {
            const a = new Audio(src);
            a.loop   = true;
            a.volume = volume;
            a.play().catch(() => {});
            bgmRef.current = a;
        } catch (_) {}
    }, [stopBGM]);

    // ── BGM unmount cleanup ───────────────────────────────────────────────────
    useEffect(() => {
        return () => {
            bgmRef.current?.pause();
            bgmRef.current = null;
            resultAudioRef.current?.pause();
            resultAudioRef.current = null;
        };
    }, []);

    // ── Mobile scroll lock ────────────────────────────────────────────────────
    useEffect(() => {
        if (phase === "result") return; // result page is scrollable
        window.scrollTo(0, 0); // scroll to top before locking
        const prev = (e) => e.preventDefault();
        document.addEventListener("touchmove", prev, { passive: false });
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("touchmove", prev);
            document.body.style.overflow = "";
        };
    }, [phase]);

    // ── 2-frame image animation ───────────────────────────────────────────────
    useEffect(() => {
        const id = setInterval(() => setAnimFrame((f) => 1 - f), 500);
        return () => clearInterval(id);
    }, []);

    // ── Auto-play audio when JP is on front ──────────────────────────────────
    useEffect(() => {
        if (phase !== "quiz" || frontMode !== "jp") return;
        const c = cards[cardIdx];
        if (!c) return;
        const src = getAudioSrc(c);
        if (!src) return;
        if (cardAudioRef.current) { cardAudioRef.current.pause(); cardAudioRef.current = null; }
        audioPlayingRef.current = true;
        const a = playSound(src);
        cardAudioRef.current = a;
        if (a) a.onended = () => { audioPlayingRef.current = false; };
    }, [phase, cardIdx, frontMode]); // eslint-disable-line

    // ── Pile wiggle animation ─────────────────────────────────────────────────
    useEffect(() => {
        if (phase !== "pile") return;
        let tick = 0;
        const id = setInterval(() => { tick++; setPileAnim(tick); }, 900);
        return () => clearInterval(id);
    }, [phase]);

    // ── Advance card ──────────────────────────────────────────────────────────
    const advance = useCallback(() => {
        const next = cardIdx + 1;
        if (next >= cards.length) {
            setPhase("goodjob");
            const gjSrc = GOODJOB_AUDIO[language.toLowerCase()] || GOODJOB_AUDIO.en;
            const goResult = () => {
                setPhase("result");
                startBGM(RESULT_BGM_SRC, 0.4);
            };
            if (gjSrc) {
                let done = false;
                const gj = new Audio(gjSrc);
                gj.onended = () => { if (!done) { done = true; goResult(); } };
                gj.play().catch(() => { if (!done) { done = true; goResult(); } });
                setTimeout(() => { if (!done) { done = true; gj.pause(); goResult(); } }, 8000);
            } else {
                setTimeout(goResult, 2200);
            }
        } else {
            setCardIdx(next);
            setRevealed(false);
            swipingRef.current = false;
        }
    }, [cardIdx, cards.length, language, startBGM, stopBGM]);

    // ── Swipe handler ─────────────────────────────────────────────────────────
    const handleSwipe = useCallback((dir) => {
        if (swipingRef.current) return;
        if (audioPlayingRef.current) return;
        swipingRef.current = true;
        clearInterval(timerRef.current);
        setSwipeDir(dir);
        if (dir === "up") {
            setScore((s) => s + 1);
            playSound(CORRECT_SND, 0.3);
        } else {
            setWrongCards((prev) => {
                const card = cards[cardIdx];
                if (!card) return prev;
                if (prev.some((c) => c.key === card.key)) return prev;
                return [...prev, card];
            });
            playSound(WRONG_SND, 0.3);
        }
        setTimeout(() => { setSwipeDir(null); advance(); }, 380);
    }, [advance, cards, cardIdx]);

    handleSwipeRef.current = handleSwipe;

    // handleTimeout: flip card, play word audio, then swipe down after a pause
    const handleTimeout = useCallback(() => {
        const c = cards[cardIdx];
        setRevealed(true);
        if (c) {
            const src = getAudioSrc(c);
            if (src) {
                if (cardAudioRef.current) { cardAudioRef.current.pause(); cardAudioRef.current = null; }
                cardAudioRef.current = playSound(src);
            }
        }
        setTimeout(() => handleSwipeRef.current("down"), 2200);
    }, [cards, cardIdx]);
    handleTimeoutRef.current = handleTimeout;

    // ── Timer ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (phase !== "quiz") return;
        setTimeLeft(TIMER_SECS);
        swipingRef.current = false;
        countdownPlayedRef.current = false;
        clearInterval(timerRef.current);

        timerRef.current = setInterval(() => {
            setTimeLeft((t) => {
                const next = +(t - 0.1).toFixed(1);
                // Play countdown audio when first crossing ≤3s
                if (next <= 3 && t > 3 && !countdownPlayedRef.current && COUNTDOWN_SND) {
                    countdownPlayedRef.current = true;
                    const a = new Audio(COUNTDOWN_SND);
                    a.volume = 0.3;
                    a.play().catch(() => {});
                }
                if (next <= 0) {
                    clearInterval(timerRef.current);
                    handleTimeoutRef.current();
                    return 0;
                }
                return next;
            });
        }, 100);

        return () => clearInterval(timerRef.current);
    }, [phase, cardIdx]); // eslint-disable-line

    // ── Pile shuffle on click ─────────────────────────────────────────────────
    const handlePileClick = () => {
        playSound(TAP_SND);
        if (isShuffling) return;
        setIsShuffling(true);
        setTimeout(() => {
            setPileCards(shufflePick(VOCAB_CARDS, TOTAL));
            setTimeout(() => setIsShuffling(false), 380);
        }, 320);
    };

    // ── Start quiz ────────────────────────────────────────────────────────────
    const startQuiz = () => {
        playSound(TAP_SND);
        const picked = shufflePick(VOCAB_CARDS_INDEXED, TOTAL);
        setCards(picked);
        setCardIdx(0);
        setRevealed(false);
        setScore(0);
        setSwipeDir(null);
        setWrongCards([]);
        swipingRef.current = false;
        startBGM(VOCAB_BGM_SRC, 0.45);
        setPhase("quiz");
    };

    // ── Keyboard support (desktop) ────────────────────────────────────────────
    useEffect(() => {
        if (phase !== "quiz") return;
        const onKey = (e) => {
            if (e.key === "ArrowUp") {
                e.preventDefault();
                if (!swipingRef.current && revealed) { playSound(TAP_SND); handleSwipeRef.current("up"); }
            } else if (e.key === "ArrowDown") {
                e.preventDefault();
                if (!swipingRef.current && revealed) { playSound(TAP_SND); handleSwipeRef.current("down"); }
            } else if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                if (!swipingRef.current) {
                    if (!revealed) {
                        const c = cards[cardIdx];
                        if (c) {
                            if (cardAudioRef.current) { cardAudioRef.current.pause(); cardAudioRef.current = null; }
                            const src = getAudioSrc(c);
                            if (src) {
                                audioPlayingRef.current = true;
                                const a = playSound(src);
                                cardAudioRef.current = a;
                                if (a) a.onended = () => { audioPlayingRef.current = false; };
                            }
                        }
                    }
                    playSound(TAP_SND);
                    setRevealed(r => !r);
                }
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [phase, cardIdx, cards]); // eslint-disable-line

    // ── Pointer gestures ──────────────────────────────────────────────────────
    const onPointerDown  = (e) => { pointerStart.current = { x: e.clientX, y: e.clientY }; };
    const onPointerUp    = (e) => {
        const start = pointerStart.current;
        if (!start || swipingRef.current) return;
        pointerStart.current = null;
        const dy = e.clientY - start.y;
        const dx = e.clientX - start.x;
        if (Math.abs(dy) >= 55 && Math.abs(dy) > Math.abs(dx)) {
            if (revealed) handleSwipe(dy < 0 ? "up" : "down");
        } else if (Math.abs(dy) < 18 && Math.abs(dx) < 18) {
            // Play word audio only on EN/ZH side (before reveal)
            if (!revealed) {
                const c = cards[cardIdx];
                if (c) {
                    if (cardAudioRef.current) { cardAudioRef.current.pause(); cardAudioRef.current = null; }
                    const src = getAudioSrc(c);
                    if (src) {
                        audioPlayingRef.current = true;
                        const a = playSound(src);
                        cardAudioRef.current = a;
                        if (a) a.onended = () => { audioPlayingRef.current = false; };
                    }
                }
            }
            playSound(TAP_SND);
            setRevealed((r) => !r);
        }
    };
    const onPointerCancel = () => { pointerStart.current = null; };

    const card       = cards[cardIdx];
    const timerPct   = (timeLeft / TIMER_SECS) * 100;
    const timerColor = timeLeft > TIMER_SECS * 0.5 ? "#2797c4" : timeLeft > TIMER_SECS * 0.25 ? "#f59e0b" : "#ef4444";
    // SVG circle: r=15, circumference ≈ 94.25
    const CIRC = 2 * Math.PI * 15;

    // ─────────────────────────────────────────────────────────────────────────
    // GOOD JOB
    if (phase === "goodjob") {
        return (
            <div className="min-h-[100dvh] flex items-center justify-center bg-[#fffaf0]">
                <motion.p
                    initial={{ scale: 0.3, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 16 }}
                    className={`text-5xl font-bold text-[#b5435f] text-center px-4 ${fontClass}`}>
                    {t.goodjob}
                </motion.p>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // RESULT
    if (phase === "result") {
        return (
            <>
            {showCopyToast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-800/90 text-white text-xs rounded-xl px-4 py-2.5 shadow-lg flex items-center gap-2 pointer-events-none">
                    <span>🔒</span>
                    <span>© Fujiyama, Inc. {t.copyright}</span>
                </div>
            )}
            <div className="min-h-[100dvh] flex flex-col items-center bg-[#fffaf0] pt-16 pb-10 px-4 gap-6 overflow-y-auto">
                <div className="text-center">
                    <p className="text-7xl font-bold text-[#b5435f]">
                        {score}
                        <span className="text-3xl text-gray-400 font-normal"> / {TOTAL}</span>
                    </p>
                    <p className={`text-gray-500 mt-2 text-sm ${fontClass}`}>
                        {score === TOTAL ? t.perfect : score >= 7 ? t.great : score >= 4 ? t.tryHard : t.tryAgain}
                    </p>
                </div>

                {wrongCards.length > 0 && (
                    <div className="w-full max-w-sm">
                        <p className={`text-sm font-bold text-gray-400 mb-3 uppercase tracking-wide ${fontClass}`}>{t.reviewLabel}</p>
                        <div className="flex flex-col gap-3">
                            {wrongCards.map((wc, i) => {
                                const { img1, img2 } = getCardImages(wc);
                                return (
                                    <button
                                        key={wc.key + i}
                                        onClick={() => {
                                            const src = getAudioSrc(wc);
                                            if (!src) return;
                                            if (resultAudioRef.current) { resultAudioRef.current.pause(); resultAudioRef.current = null; }
                                            const a = new Audio(src);
                                            a.play().catch(() => {});
                                            resultAudioRef.current = a;
                                        }}
                                        className="bg-white rounded-2xl shadow-sm p-3 flex items-center gap-3 w-full text-left active:scale-[0.98] transition-transform">
                                        {(img1 || img2) && (() => {
                                            const hasTwoDiff = img1 && img2 && img1 !== img2;
                                            return (
                                                <div className="h-16 w-16 rounded-xl flex-shrink-0 overflow-hidden relative">
                                                    <img src={img1 || img2} alt={wc.jp} draggable={false} onContextMenu={blockContextMenu}
                                                        className="absolute inset-0 w-full h-full object-contain select-none"
                                                        style={{ opacity: hasTwoDiff ? (animFrame === 0 ? 1 : 0) : 1, transition: "opacity 0.1s" }} />
                                                    {hasTwoDiff && (
                                                        <img src={img2} alt="" draggable={false} onContextMenu={blockContextMenu}
                                                            className="absolute inset-0 w-full h-full object-contain select-none"
                                                            style={{ opacity: animFrame === 1 ? 1 : 0, transition: "opacity 0.1s" }} />
                                                    )}
                                                </div>
                                            );
                                        })()}
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm text-[#2797c4] font-en">{wc.romaji}</p>
                                            <p className="text-xl font-bold text-gray-800 font-jp">{wc.jp}</p>
                                            {(wc.zh || wc.en) && (
                                                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                                    {wc.en && <span className="text-sm text-gray-400 font-en">{wc.en}</span>}
                                                    {wc.zh && <span className="text-sm text-gray-400 font-zh">{wc.zh}</span>}
                                                </div>
                                            )}
                                        </div>
                                        <i className="fa-solid fa-headphones text-[#b5435f] opacity-40 flex-shrink-0 text-sm" />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {score === TOTAL && HANAMARU_VIDEO && (
                    <div className="pointer-events-none flex justify-center">
                        <HanamaruCanvas videoSrc={HANAMARU_VIDEO} />
                    </div>
                )}

                <button
                    onClick={() => {
                        playSound(TAP_SND);
                        stopBGM();
                        setPileCards(shufflePick(VOCAB_CARDS_INDEXED, TOTAL));
                        setPhase("pile");
                    }}
                    className={`px-8 py-3 rounded-full bg-[#b5435f] text-white font-bold text-base shadow-lg active:scale-95 transition-transform ${fontClass}`}>
                    {t.playAgain}
                </button>
            </div>
            </>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // PILE (start screen)
    if (phase === "pile") {
        const topIdx = pileCards.length - 1;
        return (
            <div className="h-[100dvh] max-h-[100dvh] flex flex-col items-center justify-center gap-5 bg-[#fffaf0] px-4 pt-[60px] pb-8 overflow-hidden">
                {showCopyToast && (
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-800/90 text-white text-xs rounded-xl px-4 py-2.5 shadow-lg flex items-center gap-2 pointer-events-none">
                        <span>🔒</span>
                        <span>© Fujiyama, Inc. {t.copyright}</span>
                    </div>
                )}
                <h1 className="sr-only">{t.title}</h1>

                {/* Title */}
                <div className="text-center flex-shrink-0">
                    <p className={`text-xl font-bold text-[#b5435f] leading-snug ${fontClass}`}>
                        {frontMode === "jp" ? t.titleJpFront : t.title}
                    </p>
                </div>

                {/* Card pile */}
                <div
                    className="relative flex-shrink-0 cursor-pointer"
                    style={{ width: 210, height: 300 }}
                    onClick={handlePileClick}>
                    {pileCards.map((c, i) => {
                        const { img1, img2 } = getCardImages(c);
                        const displayed = img2 || img1;
                        const isTop     = i === topIdx;
                        const rot       = PILE_ROTS[i % PILE_ROTS.length];
                        const offX      = PILE_OFFX[i % PILE_OFFX.length];
                        const midIdx    = (pileCards.length - 1) / 2;
                        const spread    = isShuffling ? (i - midIdx) : 0;
                        const animRot   = isShuffling ? rot + spread * 9 : (isTop ? rot + Math.sin(pileAnim * 0.9) * 2.5 : rot);
                        const animX     = isShuffling ? offX + spread * 16 : offX;
                        const animY     = isShuffling ? -10 : (isTop ? Math.sin(pileAnim * 0.7) * 3 : 0);
                        return (
                            <div
                                key={c.key + i}
                                className="absolute inset-0"
                                style={{
                                    transform: `rotate(${animRot}deg) translateX(${animX}px) translateY(${animY}px)`,
                                    zIndex: i,
                                    transition: "transform 0.35s ease",
                                }}>
                                <div className="w-full h-full bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden flex flex-col">
                                    {/* Blank text area — matches quiz card structure */}
                                    <div className="flex-shrink-0" style={{ height: '30%' }} />
                                    {/* Image fills bottom 70% */}
                                    <div className="flex-1 min-h-0 overflow-hidden">
                                        {displayed
                                            ? <img src={displayed} alt={c.jp} draggable={false} onContextMenu={blockContextMenu} className="w-full h-full object-contain select-none" />
                                            : <div className="w-full h-full flex items-center justify-center text-3xl font-jp text-gray-300">{c.jp}</div>
                                        }
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* How to play */}
                <div className={`text-center flex-shrink-0 px-2 ${fontClass}`}>
                    <p className="text-sm text-gray-500">{frontMode === "jp" ? t.howToPlayJpFront : t.howToPlay}</p>
                    {/* Mobile instructions */}
                    <p className="text-sm text-gray-400 mt-0.5 md:hidden">{t.howToPlay2}</p>
                    {/* Desktop instructions */}
                    <p className="text-sm text-gray-400 mt-0.5 hidden md:block">{t.howToPlayPC}</p>
                </div>

                {/* Front side toggle */}
                <div className={`flex items-center gap-3 flex-shrink-0 ${fontClass}`}>
                    <div className="flex border-2 border-[#b5435f] rounded-full overflow-hidden">
                        {[
                            { mode: "en", label: t.enFront },
                            { mode: "jp", label: t.jpFront },
                        ].map(({ mode, label }) => (
                            <button key={mode}
                                onClick={() => { playSound(TAP_SND); setFrontMode(mode); }}
                                className={`px-4 py-1.5 text-sm font-semibold transition
                                    ${frontMode === mode
                                        ? "bg-[#b5435f] text-white"
                                        : "text-[#b5435f] hover:bg-[#b5435f]/5"}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                    <span className="text-sm text-gray-400">{t.frontLabel}</span>
                </div>

                {/* Start button */}
                <button
                    onClick={startQuiz}
                    className={`flex-shrink-0 px-12 py-4 rounded-full text-white text-xl font-bold shadow-lg bg-[#b5435f] active:scale-95 transition-transform ${fontClass}`}>
                    {t.start}
                </button>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // QUIZ
    if (!card) return null;
    const { img1, img2 }   = getCardImages(card);
    const hasTwoDifferent  = img1 && img2 && img1 !== img2;
    const frameImg         = hasTwoDifferent ? (animFrame === 0 ? img1 : img2) : (img1 || img2);

    const cardAnimate = swipeDir === "up"
        ? { y: "-130%", opacity: 0, rotate: -8 }
        : swipeDir === "down"
        ? { y: "130%",  opacity: 0, rotate: 8 }
        : { y: 0,       opacity: 1, rotate: 0 };

    return (
        <div className="h-[100dvh] max-h-[100dvh] flex flex-col items-center bg-[#fffaf0] px-3 pt-[60px] pb-2 select-none touch-none overflow-hidden">

            {/* Copyright toast */}
            {showCopyToast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-800/90 text-white text-xs rounded-xl px-4 py-2.5 shadow-lg flex items-center gap-2 pointer-events-none">
                    <span>🔒</span>
                    <span>© Fujiyama, Inc. {t.copyright}</span>
                </div>
            )}

            {/* Header: exit + progress + circular timer */}
            <div className="w-full max-w-sm flex items-center gap-3 mb-2">
                <button
                    onClick={() => {
                        playSound(TAP_SND);
                        stopBGM();
                        clearInterval(timerRef.current);
                        setPhase("pile");
                    }}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-white shadow text-gray-500 flex-shrink-0"
                    style={{ fontSize: 14 }}>
                    <i className="fa-solid fa-right-from-bracket" style={{ display: "inline-block", transform: "scaleX(-1)" }} />
                </button>

                <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                        className="h-2 rounded-full bg-[#2797c4] transition-all duration-300"
                        style={{ width: `${(cardIdx / TOTAL) * 100}%` }} />
                </div>

                {/* Circular timer */}
                <svg width="40" height="40" viewBox="0 0 38 38" className="flex-shrink-0" style={{ overflow: "visible" }}>
                    <circle cx="19" cy="19" r="15" fill="white" stroke="#e5e7eb" strokeWidth="3.5" />
                    <circle cx="19" cy="19" r="15" fill="none"
                        stroke={timerColor}
                        strokeWidth="3.5"
                        strokeDasharray={CIRC}
                        strokeDashoffset={CIRC * (1 - timerPct / 100)}
                        strokeLinecap="round"
                        transform="rotate(-90 19 19)"
                        style={{ transition: "stroke-dashoffset 0.1s linear, stroke 0.3s ease" }} />
                    <text x="19" y="24" textAnchor="middle" fontSize="13" fontWeight="bold"
                        fill={timerColor} style={{ fontFamily: "system-ui, sans-serif" }}>
                        {Math.ceil(timeLeft)}
                    </text>
                </svg>

                {/* Card counter */}
                <span className="text-sm font-bold text-[#b5435f] flex-shrink-0">
                    {cardIdx + 1}<span className="font-normal text-gray-400"> / {TOTAL}</span>
                </span>
            </div>

            {/* Swipe-up hint — mobile only, tappable, only after reveal */}
            {revealed ? (
                <motion.button
                    className="flex-shrink-0 flex items-center justify-center h-10 md:hidden"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                    onClick={() => { if (!swipingRef.current) { playSound(TAP_SND); handleSwipe("up"); } }}>
                    <div className="flex items-center gap-1.5 bg-green-100 text-green-600 rounded-full px-4 py-1.5 active:scale-95 transition-transform">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="18 15 12 9 6 15"/>
                        </svg>
                        <span className={`text-sm font-bold ${fontClass}`}>{t.hintUp}</span>
                    </div>
                </motion.button>
            ) : (
                <div className="flex-shrink-0 h-10 md:hidden" />
            )}

            {/* Card */}
            <motion.div
                key={cardIdx}
                initial={{ y: 60, opacity: 0, scale: 0.95 }}
                animate={cardAnimate}
                transition={swipeDir ? { duration: 0.35, ease: "easeIn" } : { duration: 0.3, ease: "easeOut" }}
                className="w-full max-w-sm bg-white rounded-3xl shadow-xl flex flex-col overflow-hidden flex-1 min-h-0"
                style={{ maxHeight: "calc(100dvh - 220px)" }}
                onPointerDown={onPointerDown}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}>

                {/* Text area */}
                <div className="px-5 pt-5 pb-3 text-center flex-shrink-0">
                    <AnimatePresence mode="wait">
                        {!revealed ? (
                            <motion.div key="question"
                                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.2 }}>
                                {frontMode === "jp" ? (
                                    <>
                                        <p className="text-xl font-semibold text-[#2797c4] tracking-wide font-en">{card.romaji}</p>
                                        <p className="text-3xl font-bold text-gray-800 font-jp">{card.jp}</p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-2xl font-bold text-gray-800 leading-snug font-zh">
                                            {card.zh || <span className="text-gray-300 text-base font-normal">—</span>}
                                        </p>
                                        <p className="text-xl text-gray-500 mt-1 font-en">
                                            {card.en || <span className="text-gray-300 text-base">—</span>}
                                        </p>
                                    </>
                                )}
                            </motion.div>
                        ) : (
                            <motion.div key="answer"
                                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.2 }}>
                                {frontMode === "jp" ? (
                                    <>
                                        <p className="text-2xl font-bold text-gray-800 leading-snug font-zh">
                                            {card.zh || <span className="text-gray-300 text-base font-normal">—</span>}
                                        </p>
                                        <p className="text-xl text-gray-500 mt-1 font-en">
                                            {card.en || <span className="text-gray-300 text-base">—</span>}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-xl font-semibold text-[#2797c4] tracking-wide font-en">{card.romaji}</p>
                                        <p className="text-3xl font-bold text-gray-800 font-jp">{card.jp}</p>
                                    </>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Image area — full card width, fills remaining height */}
                {/* Hidden on front side when en mode (image is the "answer") */}
                <div className="flex-1 min-h-0 overflow-hidden relative">
                    {/* First-play hint — first card each session */}
                    {cardIdx === 0 && !revealed && (
                        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                            <span className={`bg-white/90 text-gray-700 text-lg font-bold rounded-2xl px-6 py-3 shadow-md ${fontClass}`}>
                                {t.tapHint}
                            </span>
                        </div>
                    )}
                    {frameImg && (frontMode === "en" || revealed) ? (
                        <div className="w-full h-full relative">
                            <img src={img1} alt={card.jp} draggable={false} onContextMenu={blockContextMenu}
                                className="absolute inset-0 w-full h-full object-contain select-none"
                                style={{ opacity: hasTwoDifferent ? (animFrame === 0 ? 1 : 0) : 1,
                                         transition: "opacity 0.1s" }} />
                            {hasTwoDifferent && (
                                <img src={img2} alt="" draggable={false} onContextMenu={blockContextMenu}
                                    className="absolute inset-0 w-full h-full object-contain select-none"
                                    style={{ opacity: animFrame === 1 ? 1 : 0,
                                             transition: "opacity 0.1s" }} />
                            )}
                        </div>
                    ) : null}

                </div>
            </motion.div>

            {/* Swipe-down hint — mobile only, tappable, only after reveal */}
            {revealed ? (
                <motion.button
                    className="flex-shrink-0 flex items-center justify-center h-10 mt-1 md:hidden"
                    animate={{ y: [0, 5, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut", delay: 0.2 }}
                    onClick={() => { if (!swipingRef.current) { playSound(TAP_SND); handleSwipe("down"); } }}>
                    <div className="flex items-center gap-1.5 bg-red-100 text-red-500 rounded-full px-4 py-1.5 active:scale-95 transition-transform">
                        <span className={`text-sm font-bold ${fontClass}`}>{t.hintDown}</span>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9"/>
                        </svg>
                    </div>
                </motion.button>
            ) : (
                <div className="flex-shrink-0 h-10 mt-1 md:hidden" />
            )}

            {/* Desktop buttons — visible on md+, hidden on mobile, only after reveal */}
            {revealed ? (
                <div className="hidden md:flex flex-col w-full max-w-sm gap-1">
                    <div className="flex gap-3">
                        <button
                            onClick={() => { if (!swipingRef.current) { playSound(TAP_SND); handleSwipe("down"); } }}
                            className={`flex-1 py-2.5 rounded-full bg-red-50 text-red-500 font-bold text-sm border border-red-200 hover:bg-red-100 active:scale-95 transition-all ${fontClass}`}>
                            ↓ {t.btnNoIdea}
                        </button>
                        <button
                            onClick={() => { if (!swipingRef.current) { playSound(TAP_SND); handleSwipe("up"); } }}
                            className={`flex-1 py-2.5 rounded-full bg-green-50 text-green-600 font-bold text-sm border border-green-200 hover:bg-green-100 active:scale-95 transition-all ${fontClass}`}>
                            ↑ {t.btnKnow}
                        </button>
                    </div>
                    <p className="text-center text-xs text-gray-300">arrow keys · Space to flip</p>
                </div>
            ) : (
                <div className="hidden md:block h-[62px] w-full max-w-sm" />
            )}
        </div>
    );
}
